import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { 
  RefreshCw, 
  Calendar, 
  Users, 
  CheckCircle, 
  AlertCircle,
  ArrowRight,
  ArrowLeft,
  Building,
  Award,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

interface EntitySelection {
  teams: boolean;
  venues: boolean;
  coaches: boolean;
}

interface RolloverSummary {
  entityType: string;
  count: number;
  currentSeason: string;
}

type WizardStep = 'select' | 'configure' | 'preview' | 'execute' | 'complete';

export const SeasonRolloverWizard = () => {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState<WizardStep>('select');
  const [entitySelection, setEntitySelection] = useState<EntitySelection>({
    teams: false,
    venues: false,
    coaches: false,
  });
  const [newSeasonName, setNewSeasonName] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [summary, setSummary] = useState<RolloverSummary[]>([]);
  const [rolloverResults, setRolloverResults] = useState<any[]>([]);

  const hasSelection = Object.values(entitySelection).some(v => v);

  const loadEntityCounts = async () => {
    setLoading(true);
    try {
      const summaryData: RolloverSummary[] = [];

      if (entitySelection.teams) {
        const { count } = await supabase
          .from('teams')
          .select('*', { count: 'exact', head: true });
        summaryData.push({
          entityType: 'Teams',
          count: count || 0,
          currentSeason: '2024-2025', // This would come from actual season data
        });
      }

      if (entitySelection.venues) {
        const { count } = await supabase
          .from('venues')
          .select('*', { count: 'exact', head: true });
        summaryData.push({
          entityType: 'Venues',
          count: count || 0,
          currentSeason: '2024-2025',
        });
      }

      if (entitySelection.coaches) {
        const { count } = await supabase
          .from('profiles')
          .select('*', { count: 'exact', head: true })
          .eq('role', 'coach');
        summaryData.push({
          entityType: 'Coaches',
          count: count || 0,
          currentSeason: '2024-2025',
        });
      }

      setSummary(summaryData);
    } catch (error) {
      console.error('Error loading entity counts:', error);
      toast.error('Failed to load entity information');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    if (currentStep === 'select') {
      if (!hasSelection) {
        toast.error('Please select at least one entity type');
        return;
      }
      setCurrentStep('configure');
    } else if (currentStep === 'configure') {
      if (!newSeasonName.trim()) {
        toast.error('Please enter a new season name');
        return;
      }
      await loadEntityCounts();
      setCurrentStep('preview');
    } else if (currentStep === 'preview') {
      await executeRollover();
    }
  };

  const handleBack = () => {
    if (currentStep === 'configure') {
      setCurrentStep('select');
    } else if (currentStep === 'preview') {
      setCurrentStep('configure');
    }
  };

  const executeRollover = async () => {
    if (!user) return;

    setCurrentStep('execute');
    setProgress(0);
    setLoading(true);

    try {
      const results: any[] = [];
      const selectedTypes = Object.entries(entitySelection)
        .filter(([_, selected]) => selected)
        .map(([type]) => type.slice(0, -1)); // Remove 's' from plural

      let completedCount = 0;
      const totalSteps = selectedTypes.length;

      for (const entityType of selectedTypes) {
        try {
          // Create season rollover record for the entity type
          const { data, error } = await supabase
            .from('season_rollovers')
            .insert({
              entity_type: entityType as 'team' | 'venue' | 'coach',
              entity_id: 'system-wide', // Special ID for system-wide rollover
              old_season_name: '2024-2025', // This would come from actual season data
              new_season_name: newSeasonName.trim(),
              initiated_by: user.id,
              rollover_status: 'completed',
              notes: notes.trim() || null,
            })
            .select()
            .single();

          if (error) throw error;

          // Send batch email notifications to all users of this entity type
          try {
            const { data: emailData, error: emailError } = await supabase.functions.invoke(
              'send-season-rollover-batch',
              {
                body: {
                  entityType,
                  oldSeasonName: '2024-2025',
                  newSeasonName: newSeasonName.trim(),
                  effectiveDate: new Date().toISOString(),
                  notes: notes.trim() || undefined
                }
              }
            );

            if (emailError) {
              console.error('Error sending batch notifications:', emailError);
              // Don't fail the rollover if email fails
            } else {
              console.log('Batch notifications sent:', emailData);
            }
          } catch (emailError) {
            console.error('Error invoking email function:', emailError);
            // Don't fail the rollover if email fails
          }
          
          results.push({
            entityType,
            status: 'success',
            message: `Successfully initiated rollover for ${entityType}s and sent email notifications to all users.`,
            data,
          });

          completedCount++;
          setProgress((completedCount / totalSteps) * 100);
          
          // Simulate some processing time
          await new Promise(resolve => setTimeout(resolve, 1000));
        } catch (error) {
          console.error(`Error rolling over ${entityType}:`, error);
          results.push({
            entityType,
            status: 'error',
            message: `Failed to rollover ${entityType}s`,
            error,
          });
        }
      }

      setRolloverResults(results);
      setProgress(100);
      
      const successCount = results.filter(r => r.status === 'success').length;
      if (successCount === results.length) {
        toast.success('Season rollover completed successfully!');
      } else {
        toast.warning(`Rollover completed with ${results.length - successCount} errors`);
      }

      setCurrentStep('complete');
    } catch (error) {
      console.error('Error executing rollover:', error);
      toast.error('Failed to complete season rollover');
    } finally {
      setLoading(false);
    }
  };

  const resetWizard = () => {
    setCurrentStep('select');
    setEntitySelection({ teams: false, venues: false, coaches: false });
    setNewSeasonName('');
    setNotes('');
    setProgress(0);
    setSummary([]);
    setRolloverResults([]);
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 'select':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">Select Entity Types to Rollover</h3>
              <p className="text-sm text-muted-foreground mb-6">
                Choose which entity types should be rolled over to a new season. All active data will be archived and new season structures will be created.
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-3 p-4 border rounded-lg hover:bg-muted/50 transition-colors">
                <Checkbox
                  id="teams"
                  checked={entitySelection.teams}
                  onCheckedChange={(checked) => 
                    setEntitySelection(prev => ({ ...prev, teams: checked as boolean }))
                  }
                />
                <Label htmlFor="teams" className="flex items-center gap-3 cursor-pointer flex-1">
                  <Users className="w-5 h-5 text-primary" />
                  <div>
                    <p className="font-medium">Teams</p>
                    <p className="text-sm text-muted-foreground">Rollover all team seasons</p>
                  </div>
                </Label>
              </div>

              <div className="flex items-center space-x-3 p-4 border rounded-lg hover:bg-muted/50 transition-colors">
                <Checkbox
                  id="venues"
                  checked={entitySelection.venues}
                  onCheckedChange={(checked) => 
                    setEntitySelection(prev => ({ ...prev, venues: checked as boolean }))
                  }
                />
                <Label htmlFor="venues" className="flex items-center gap-3 cursor-pointer flex-1">
                  <Building className="w-5 h-5 text-primary" />
                  <div>
                    <p className="font-medium">Venues</p>
                    <p className="text-sm text-muted-foreground">Rollover all venue seasons</p>
                  </div>
                </Label>
              </div>

              <div className="flex items-center space-x-3 p-4 border rounded-lg hover:bg-muted/50 transition-colors">
                <Checkbox
                  id="coaches"
                  checked={entitySelection.coaches}
                  onCheckedChange={(checked) => 
                    setEntitySelection(prev => ({ ...prev, coaches: checked as boolean }))
                  }
                />
                <Label htmlFor="coaches" className="flex items-center gap-3 cursor-pointer flex-1">
                  <Award className="w-5 h-5 text-primary" />
                  <div>
                    <p className="font-medium">Coaches</p>
                    <p className="text-sm text-muted-foreground">Rollover all coach seasons</p>
                  </div>
                </Label>
              </div>
            </div>
          </div>
        );

      case 'configure':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">Configure New Season</h3>
              <p className="text-sm text-muted-foreground mb-6">
                Set up the details for the new season. All selected entities will use these settings.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="newSeasonName">New Season Name *</Label>
                <Input
                  id="newSeasonName"
                  placeholder="e.g., 2025-2026, Spring 2025"
                  value={newSeasonName}
                  onChange={(e) => setNewSeasonName(e.target.value)}
                  className="mt-1.5"
                />
              </div>

              <div>
                <Label htmlFor="notes">Rollover Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  placeholder="Add any notes about this season transition..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={4}
                  className="mt-1.5"
                />
              </div>

              <div className="bg-muted/50 p-4 rounded-lg space-y-2">
                <h4 className="font-medium text-sm flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-primary" />
                  Selected Entity Types:
                </h4>
                <div className="flex flex-wrap gap-2 ml-6">
                  {entitySelection.teams && (
                    <Badge variant="secondary">Teams</Badge>
                  )}
                  {entitySelection.venues && (
                    <Badge variant="secondary">Venues</Badge>
                  )}
                  {entitySelection.coaches && (
                    <Badge variant="secondary">Coaches</Badge>
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      case 'preview':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">Review Rollover Summary</h3>
              <p className="text-sm text-muted-foreground mb-6">
                Review the entities that will be rolled over. Click "Execute Rollover" to proceed.
              </p>
            </div>

            <div className="bg-muted/50 p-4 rounded-lg space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-medium">New Season Name:</span>
                  <Badge variant="default">{newSeasonName}</Badge>
                </div>
                {notes && (
                  <div className="space-y-1">
                    <span className="font-medium text-sm">Notes:</span>
                    <p className="text-sm text-muted-foreground">{notes}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-3">
              {summary.map((item) => (
                <Card key={item.entityType}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{item.entityType}</p>
                        <p className="text-sm text-muted-foreground">
                          {item.count} entities • Current: {item.currentSeason}
                        </p>
                      </div>
                      <CheckCircle className="w-5 h-5 text-primary" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
              <div className="flex gap-2">
                <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-yellow-900 dark:text-yellow-100 mb-1">
                    Important Notice
                  </p>
                  <p className="text-yellow-800 dark:text-yellow-200">
                    This action will archive the current season for all selected entity types. 
                    All data is preserved and accessible in season archives, but this action cannot be undone.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'execute':
        return (
          <div className="space-y-6">
            <div className="text-center space-y-4">
              <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto" />
              <div>
                <h3 className="text-lg font-semibold mb-2">Processing Season Rollover</h3>
                <p className="text-sm text-muted-foreground">
                  Please wait while we archive the current season and create new season structures...
                </p>
              </div>
              <Progress value={progress} className="w-full" />
              <p className="text-sm text-muted-foreground">{Math.round(progress)}% complete</p>
            </div>
          </div>
        );

      case 'complete':
        return (
          <div className="space-y-6">
            <div className="text-center space-y-4">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto" />
              <div>
                <h3 className="text-lg font-semibold mb-2">Season Rollover Complete!</h3>
                <p className="text-sm text-muted-foreground">
                  All selected entities have been rolled over to {newSeasonName}
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="font-medium">Rollover Results:</h4>
              {rolloverResults.map((result, index) => (
                <Card key={index}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {result.status === 'success' ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-red-500" />
                        )}
                        <div>
                          <p className="font-medium capitalize">{result.entityType}s</p>
                          <p className="text-sm text-muted-foreground">{result.message}</p>
                        </div>
                      </div>
                      <Badge variant={result.status === 'success' ? 'default' : 'destructive'}>
                        {result.status}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Button onClick={resetWizard} className="w-full" size="lg">
              Start New Rollover
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <RefreshCw className="w-5 h-5 text-primary" />
          <div className="flex-1">
            <CardTitle>Season Rollover Wizard</CardTitle>
            <CardDescription>
              Archive current seasons and start new ones across multiple entity types
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Progress Steps */}
        {currentStep !== 'complete' && (
          <div className="flex items-center justify-between mb-8">
            {(['select', 'configure', 'preview', 'execute'] as const).map((step, index) => (
              <div key={step} className="flex items-center">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                  currentStep === step ? 'border-primary bg-primary text-primary-foreground' :
                  ['select', 'configure', 'preview', 'execute'].indexOf(currentStep) > index ? 'border-primary bg-primary text-primary-foreground' :
                  'border-muted bg-background text-muted-foreground'
                }`}>
                  {index + 1}
                </div>
                {index < 3 && (
                  <div className={`w-16 h-0.5 mx-2 ${
                    ['select', 'configure', 'preview', 'execute'].indexOf(currentStep) > index ? 'bg-primary' : 'bg-muted'
                  }`} />
                )}
              </div>
            ))}
          </div>
        )}

        {/* Step Content */}
        <div className="min-h-[400px]">
          {renderStepContent()}
        </div>

        {/* Navigation Buttons */}
        {currentStep !== 'execute' && currentStep !== 'complete' && (
          <div className="flex justify-between gap-4 pt-6 border-t">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={currentStep === 'select' || loading}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <Button
              onClick={handleNext}
              disabled={loading || (currentStep === 'select' && !hasSelection) || (currentStep === 'configure' && !newSeasonName.trim())}
              size="lg"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Loading...
                </>
              ) : currentStep === 'preview' ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Execute Rollover
                </>
              ) : (
                <>
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
